import { clamp } from '../units/round.js';

/**
 * Limites duros del dominio. Se aplican DESPUES de cualquier decision, venga del
 * algoritmo, de Claude o de una propagacion automatica (ADR-020).
 * Fuente unica: docs/SECURITY_PRIVACY.md §6.2.
 */
export const SAFETY_LIMITS = {
  energy: {
    /** Suelo absoluto en kcal; se combina con RMR x 1,1. */
    absoluteFloorKcal: 1600,
    rmrFloorFactor: 1.1,
    maxDeficitPct: 0.25,
    maxSurplusPct: 0.15,
  },
  weight: {
    maxWeeklyLossPct: 0.01,
    warnWeeklyLossPct: 0.008,
    maxWeeklyGainPct: 0.005,
  },
  macros: {
    minProteinGPerKg: 1.6,
    minFatGPerKg: 0.6,
    minFatPctOfKcal: 0.2,
  },
  load: {
    /** Salto maximo de carga entre sesiones del mismo ejercicio. */
    maxSessionToSessionChangePct: 0.1,
    /** Ajuste acumulado maximo dentro de un mismo ejercicio. */
    maxIntraSessionChangePct: 0.1,
  },
  volume: {
    maxWeeklyChangePct: 0.2,
    maxEffectiveSetsPerSession: 30,
  },
  population: {
    /** Por debajo, el plan no se crea: orientación limitada y derivación (safety/population.ts). */
    minAdultAgeYears: 18,
  },
  session: {
    maxMinutes: 90,
    maxStrengthSessionsPerWeek: 5,
    maxConsecutiveHardDays: 3,
  },
} as const;

export interface ClampResult<T> {
  value: T;
  clamped: boolean;
  reasonCodes: string[];
}

/** Recorta una carga para que no supere el salto permitido entre sesiones. */
export function clampSessionLoad(rawKg: number, previousSessionKg: number | null): ClampResult<number> {
  if (previousSessionKg === null || previousSessionKg <= 0) {
    return { value: rawKg, clamped: false, reasonCodes: [] };
  }
  const max = previousSessionKg * (1 + SAFETY_LIMITS.load.maxSessionToSessionChangePct);
  const min = previousSessionKg * (1 - SAFETY_LIMITS.load.maxSessionToSessionChangePct);
  const value = clamp(rawKg, min, max);
  return {
    value,
    clamped: value !== rawKg,
    reasonCodes: value !== rawKg ? ['clamped_by_safety_limit'] : [],
  };
}

/** Suelo calorico efectivo: el mayor entre el absoluto y RMR x factor. */
export function energyFloorKcal(rmrKcal: number): number {
  return Math.max(
    SAFETY_LIMITS.energy.absoluteFloorKcal,
    Math.round(rmrKcal * SAFETY_LIMITS.energy.rmrFloorFactor),
  );
}

/** Recorta un objetivo energetico a los limites de deficit, superavit y suelo. */
export function clampEnergyTarget(
  targetKcal: number,
  context: { tdeeKcal: number; rmrKcal: number },
): ClampResult<number> {
  const reasonCodes: string[] = [];
  const floor = energyFloorKcal(context.rmrKcal);
  const minByDeficit = context.tdeeKcal * (1 - SAFETY_LIMITS.energy.maxDeficitPct);
  const maxBySurplus = context.tdeeKcal * (1 + SAFETY_LIMITS.energy.maxSurplusPct);

  let value = targetKcal;
  if (value < minByDeficit) {
    value = minByDeficit;
    reasonCodes.push('clamped_max_deficit');
  }
  if (value > maxBySurplus) {
    value = maxBySurplus;
    reasonCodes.push('clamped_max_surplus');
  }
  if (value < floor) {
    value = floor;
    reasonCodes.push('clamped_energy_floor');
  }

  const rounded = Math.round(value);
  return { value: rounded, clamped: rounded !== Math.round(targetKcal), reasonCodes };
}

/** Recorta el volumen semanal para no variar mas del limite respecto al inicio del bloque. */
export function clampWeeklyVolume(
  proposedSets: number,
  blockStartSets: number,
): ClampResult<number> {
  if (blockStartSets <= 0) return { value: proposedSets, clamped: false, reasonCodes: [] };
  const max = blockStartSets * (1 + SAFETY_LIMITS.volume.maxWeeklyChangePct);
  const min = blockStartSets * (1 - SAFETY_LIMITS.volume.maxWeeklyChangePct);
  const value = Math.round(clamp(proposedSets, min, max));
  return {
    value,
    clamped: value !== proposedSets,
    reasonCodes: value !== proposedSets ? ['clamped_by_safety_limit'] : [],
  };
}
